type HSL = { h: number; s: number; l: number };

function hexToHsl(hex: string): HSL {
  const sanitized = hex.replace("#", "");
  const r = parseInt(sanitized.substring(0, 2), 16) / 255;
  const g = parseInt(sanitized.substring(2, 4), 16) / 255;
  const b = parseInt(sanitized.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) {
    return { h: 0, s: 0, l };
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h: number;
  switch (max) {
    case r:
      h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      break;
    case g:
      h = ((b - r) / d + 2) / 6;
      break;
    default:
      h = ((r - g) / d + 4) / 6;
      break;
  }

  return { h: h * 360, s, l };
}

function hueToRgb(p: number, q: number, t: number): number {
  let tNorm = t;
  if (tNorm < 0) tNorm += 1;
  if (tNorm > 1) tNorm -= 1;
  if (tNorm < 1 / 6) return p + (q - p) * 6 * tNorm;
  if (tNorm < 1 / 2) return q;
  if (tNorm < 2 / 3) return p + (q - p) * (2 / 3 - tNorm) * 6;
  return p;
}

function hslToHex(h: number, s: number, l: number): string {
  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const hNorm = h / 360;
    r = hueToRgb(p, q, hNorm + 1 / 3);
    g = hueToRgb(p, q, hNorm);
    b = hueToRgb(p, q, hNorm - 1 / 3);
  }

  const toHex = (c: number) =>
    Math.round(Math.min(255, Math.max(0, c * 255)))
      .toString(16)
      .padStart(2, "0");

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Target lightness values derived from the existing blue palette in global.scss.
 * Shade 900 is the anchor (the input color's lightness).
 * Lighter shades (100-800) interpolate between near-white and the base.
 * Darker shades (1000-1400) interpolate between the base and near-black.
 */
const LIGHTNESS_TARGETS: Record<number, number> = {
  100: 0.97,
  200: 0.95,
  300: 0.92,
  400: 0.87,
  500: 0.76,
  600: 0.7,
  700: 0.63,
  800: 0.52,
  900: 0.43,
  1000: 0.36,
  1100: 0.31,
  1200: 0.25,
  1300: 0.17,
  1400: 0.12,
};

const SHADE_STEPS = [100, 200, 300, 400, 500, 600, 700, 800, 900, 1000, 1100, 1200, 1300, 1400];

/**
 * Generates a 14-step color spectrum from a single base hex color.
 * The base color is treated as the 900 shade; lighter (100-800) and
 * darker (1000-1400) shades are computed via HSL lightness interpolation
 * scaled relative to the input color's actual lightness.
 */
export function generateColorSpectrum(baseHex: string): Record<number, string> {
  const base = hexToHsl(baseHex);
  const baseLTarget = LIGHTNESS_TARGETS[900];

  const spectrum: Record<number, string> = {};

  for (const step of SHADE_STEPS) {
    const targetL = LIGHTNESS_TARGETS[step];
    let l: number;

    if (step <= 900) {
      // Lighter shades: interpolate between 1.0 (white) and the base lightness,
      // using the ratio of how the reference palette distributes lightness above 900.
      const refRatio = (1 - targetL) / (1 - baseLTarget);
      l = Math.max(0, Math.min(1, 1 - refRatio * (1 - base.l)));
    } else {
      // Darker shades: interpolate between the base lightness and 0 (black),
      // using the ratio of how the reference palette distributes lightness below 900.
      const refRatio = targetL / baseLTarget;
      l = Math.max(0, Math.min(1, refRatio * base.l));
    }

    // Desaturate very light shades slightly for a natural feel
    let s = base.s;
    if (l > 0.9) {
      s = base.s * (0.6 + 0.4 * ((1 - l) / 0.1));
    } else if (l < 0.15) {
      s = base.s * (0.7 + 0.3 * (l / 0.15));
    }

    spectrum[step] = hslToHex(base.h, s, l);
  }

  return spectrum;
}

/**
 * Applies a primary color spectrum as CSS custom properties on :root.
 * Overrides --primary-100 through --primary-1400 plus --primary-default
 * and --primary-strong, which cascade to all --*-brand-* tokens.
 */
export function applyPrimaryColorSpectrum(primaryColor: string): void {
  if (!primaryColor) return;

  const spectrum = generateColorSpectrum(primaryColor);
  const root = document.documentElement;

  for (const step of SHADE_STEPS) {
    root.style.setProperty(`--primary-${step}`, spectrum[step]);
  }

  root.style.setProperty("--primary-default", spectrum[900]);
  root.style.setProperty("--primary-strong", spectrum[1100]);
}
